<script context="module" lang="ts">
  // Declarar jQuery
  declare const window: any;
</script>

<script lang="ts">
  import { service } from '@frontend/services';
  import { GetCachedToken, me } from '@frontend/user';
  import { get } from 'svelte/store';
  import { onMount, onDestroy } from 'svelte';
  import { translate, currentLanguage } from "../Translator";
  import { push } from "svelte-spa-router";
  import type { DataResponse } from '@frontend/walletapi';
  
  // Función local para formatear montos
  function formatMoney(money: Money | undefined): string {
    if (!money || money.InCents === undefined) return '$0.00';
    const amount = money.InCents / 100; // Convertir centavos a dólares
    return `$${Math.abs(amount).toFixed(2)}`;
  }

  // Función para formatear montos con signo (+ o -)
  function formatMoneyWithSign(money: Money | undefined): string {
    if (!money || money.InCents === undefined) return '$0.00';
    const amount = money.InCents / 100; // Convertir centavos a dólares
    const sign = amount >= 0 ? '+' : '-';
    return `${sign}$${Math.abs(amount).toFixed(2)}`;
  }

  interface Grupo {
    Id: number;
    Name: string | undefined;
    ParentId: number | undefined;
    Users?: any[];
    level?: number;
  }

  // Usar el tipo Money del API
  type Money = import('@frontend/walletapi').Money;

  // Usar la interfaz del API
  type DataResponse = import('@frontend/walletapi').DataResponse;
  
  interface ResumenPorGrupo {
    [key: number]: DataResponse;
  }

  let fechaInicio = new Date().toISOString().split('T')[0];
  let fechaFin = new Date().toISOString().split('T')[0];
  let loading = false;
  let error: string | null = null;
  let noResults = false;
  let grupos: Grupo[] = [];
  let gruposFiltrados: Grupo[] = [];
  let currentGroupId: number | null = null;
  let gruposSeleccionados: Set<number> = new Set();
  let todosSeleccionados = false;

  let resumenPorGrupo: ResumenPorGrupo = {};
  let menuVisible = false;
  let dropdownRef: HTMLElement | undefined;
  let showMoreColumns = false;
  let manualSelection = false; // Controlar si el usuario está haciendo selección manual
  let hasInitialData = false; // Controlar si ya se han cargado datos iniciales

  // Función para manejar clics fuera del dropdown
  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
      menuVisible = false;
    }
  }

  function toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    menuVisible = !menuVisible;
  }

  // Función para manejar el click en un item
  function handleItemClick(event: MouseEvent): void {
    event.stopPropagation(); // Evitar que el click cierre el dropdown
  }

  onMount(async () => {
    try {
      // Verificar autenticación primero
      if (!$me) {
        push("/user/Login");
        return;
      }

      document.addEventListener('click', handleClickOutside);

      // Esperar a que el store me tenga datos
      let meStore = get(me);
      if (!meStore) {
        console.log('Esperando a que se cargue la información del usuario...');
        // Esperar un poco y verificar de nuevo
        await new Promise(resolve => setTimeout(resolve, 1000));
        meStore = get(me);
        
        if (!meStore) {
          error = 'User information not available. Please refresh the page.';
          return;
        }
      }

      console.log('Store me cargado:', meStore);
      
      // Cargar grupos primero
      const gruposCargados = await cargarGrupos();
      if (!gruposCargados) return;
      
      // Aplicar filtros según el rol del usuario
      if (meStore.Role === "Owner") {
        // Owner puede ver todos los grupos
        gruposFiltrados = getAllGroupsWithStructure(grupos);
        console.log('Usuario Owner - Todos los grupos disponibles:', gruposFiltrados.map(g => g.Name));
      } else if (meStore.Role === "Admin" || meStore.Role === "Cashier") {
        // Admin y Cashier solo pueden ver su grupo asignado y sus subgrupos
        if (!meStore.GroupId) {
          error = 'User does not have a group assigned';
          console.log('Usuario Admin/Cashier sin grupo asignado');
          return;
        }
        
        currentGroupId = meStore.GroupId;
        console.log(`${meStore.Role} - Grupo asignado ID:`, currentGroupId);
        
        // Filtrar grupos según el rol del usuario
        filtrarGrupos();
        
        console.log(`${meStore.Role} - Grupos filtrados:`, gruposFiltrados.map(g => g.Name));
      } else {
        // Rol no reconocido
        error = 'User role not recognized';
        console.log('Rol no reconocido:', meStore.Role);
        return;
      }

      // NO seleccionar automáticamente todos los grupos
      todosSeleccionados = false;
      gruposSeleccionados = new Set<number>();

      // Forzar actualización del set reactivo
      gruposSeleccionados = gruposSeleccionados;
      console.log('Grupos seleccionados finales:', Array.from(gruposSeleccionados));

      // NO realizar la búsqueda inicial automáticamente
      // El usuario debe seleccionar grupos y hacer clic en "Search"
      
    } catch (err) {
      console.error('Error en onMount:', err);
      error = err instanceof Error ? err.message : 'Error initializing page';
    }
  });

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  // Reactive statement para manejar cambios en el store me
  $: {
    const meStore = get(me);
    console.log('Reactive statement - Store me:', meStore);
    console.log('Reactive statement - grupos.length:', grupos.length);
    console.log('Reactive statement - currentGroupId:', currentGroupId);
    
    if (meStore && grupos.length > 0) {
      console.log('Store me actualizado, procesando...');
      console.log('Role:', meStore.Role);
      console.log('GroupId:', meStore.GroupId);
      
      if (meStore.Role === "Owner") {
        console.log('Procesando como Owner');
        gruposFiltrados = getAllGroupsWithStructure(grupos);
        console.log('Owner - Todos los grupos disponibles:', gruposFiltrados.map(g => g.Name));
      } else if (meStore.Role === "Admin" || meStore.Role === "Cashier") {
        if (!meStore.GroupId) {
          console.log(`${meStore.Role} sin grupo asignado`);
          gruposFiltrados = [];
        } else {
          console.log(`Procesando como ${meStore.Role} con GroupId:`, meStore.GroupId);
          currentGroupId = meStore.GroupId;
          filtrarGrupos();
          console.log(`${meStore.Role} - Grupos filtrados:`, gruposFiltrados.map(g => g.Name));
        }
      } else {
        console.log('Rol no reconocido:', meStore.Role);
        gruposFiltrados = [];
      }
      
      // Forzar actualización
      gruposSeleccionados = gruposSeleccionados;
    }
  }

  function perteneceAJerarquia(grupo: Grupo, parentId: number): boolean {
    return grupo.ParentId === parentId;
  }

  function filtrarGrupos() {
    const meStore = get(me);
    console.log('Filtrando grupos para usuario:', meStore?.Role, 'con grupo ID:', currentGroupId);
    
    // Si es Owner, mostrar todos los grupos sin filtrar
    if (meStore?.Role === "Owner") {
      gruposFiltrados = getAllGroupsWithStructure(grupos);
      console.log('Owner - Todos los grupos disponibles:', gruposFiltrados.map(g => g.Name));
      return;
    }

    // Para Admin y Cashier, verificar que tengan grupo asignado
    if (meStore?.Role !== "Admin" && meStore?.Role !== "Cashier") {
      console.log('Rol no soportado para filtrado:', meStore?.Role);
      gruposFiltrados = [];
      return;
    }

    if (!currentGroupId || !grupos.length) {
      gruposFiltrados = [];
      console.log('No hay grupo actual o grupos disponibles');
      return;
    }

    // Encontrar el grupo actual
    const grupoActual = grupos.find(g => g.Id === currentGroupId);
    if (!grupoActual) {
      gruposFiltrados = [];
      console.log('No se encontró el grupo actual con ID:', currentGroupId);
      return;
    }

    console.log(`${meStore.Role} - Grupo actual encontrado:`, grupoActual.Name);

    // Lógica específica por rol
    let gruposDisponibles: Grupo[] = [];

    if (meStore?.Role === "Cashier") {
      // Cashier: ve su grupo Y su grupo padre
      gruposDisponibles = [grupoActual];
      
      console.log(`${meStore.Role} - Grupo actual:`, { id: grupoActual.Id, name: grupoActual.Name, parentId: grupoActual.ParentId });
      
      // Incluir el grupo padre si existe
      if (grupoActual.ParentId) {
        const grupoPadre = grupos.find(g => g.Id === grupoActual.ParentId);
        if (grupoPadre) {
          gruposDisponibles.unshift(grupoPadre); // Agregar al inicio
          console.log(`${meStore.Role} - Grupo padre encontrado y agregado:`, { id: grupoPadre.Id, name: grupoPadre.Name });
        } else {
          console.log(`${meStore.Role} - Grupo padre no encontrado en la lista de grupos:`, grupoActual.ParentId);
        }
      } else {
        console.log(`${meStore.Role} - Grupo actual no tiene padre`);
      }
      
      console.log(`${meStore.Role} - Grupos disponibles (actual + padre):`, gruposDisponibles.map(g => ({ id: g.Id, name: g.Name })));
      
      // Para Cashiers, usar directamente los grupos sin estructura jerárquica
      gruposFiltrados = gruposDisponibles.map(g => ({ ...g, level: 0 }));
      console.log(`${meStore.Role} - Grupos filtrados finales (sin estructura):`, gruposFiltrados.map(g => ({ id: g.Id, name: g.Name, level: g.level })));
      
    } else if (meStore?.Role === "Admin") {
      // Admin: ve su grupo Y sus subgrupos
      gruposDisponibles = [grupoActual];
      
      function obtenerHijos(groupId: number): Grupo[] {
        const hijosDirectos = grupos.filter(g => g.ParentId === groupId);
        let todosLosHijos = [...hijosDirectos];
        
        hijosDirectos.forEach(hijo => {
          todosLosHijos = [...todosLosHijos, ...obtenerHijos(hijo.Id)];
        });
        
        return todosLosHijos;
      }
      
      // Obtener el grupo actual y sus hijos
      const gruposDisponiblesCompletos = [
        ...gruposDisponibles,
        ...obtenerHijos(currentGroupId)
      ];
      
      gruposDisponibles = gruposDisponiblesCompletos;
      console.log(`${meStore.Role} - Grupos disponibles (actual + hijos):`, gruposDisponibles.map(g => g.Name));
    }

    // Aplicar la estructura jerárquica a los grupos disponibles (solo para Admin y Owner)
    if (meStore?.Role === "Admin") {
      gruposFiltrados = getAllGroupsWithStructure(gruposDisponibles);
      console.log(`${meStore.Role} - Grupos filtrados finales:`, gruposFiltrados.map(g => ({ id: g.Id, name: g.Name, level: g.level })));
    }
    // Para Cashiers, los grupos ya están procesados arriba
  }

  async function cargarGrupos() {
    try {
      const token = GetCachedToken();
      if (!token) {
        throw new Error('No token available');
      }

      console.log('Obteniendo grupos con token:', token);

      const response = await service.walletapi.groupList({
        Token: token
      });

      console.log('Respuesta de groupList:', response);

      if (!response?.Success) {
        throw new Error('Error obtaining groups: ' + (response?.Message || 'Unknown error'));
      }

      if (!response.Data || !Array.isArray(response.Data)) {
        throw new Error('Group data is not in the expected format');
      }

      grupos = response.Data;
      console.log('Grupos cargados:', grupos);

      return true;
    } catch (err) {
      console.error('Error loading groups:', err);
      error = err instanceof Error ? err.message : 'Error loading groups';
      return false;
    }
  }

  async function sumByDateDebit() {
    try {
      if (!fechaInicio || !fechaFin) {
        error = 'Please select both dates';
        return;
      }

      if (gruposSeleccionados.size === 0) {
        error = 'Please select at least one group';
        return;
      }

      // Validar que el usuario solo pueda acceder a grupos que tiene permiso para ver
      const meStore = get(me);
      if (!meStore) {
        error = 'User information not available';
        return;
      }

      // Debug: Mostrar información de validación
      console.log('Validación de permisos:');
      console.log('- Usuario:', meStore.Role, 'ID:', meStore.Id, 'GroupId:', meStore.GroupId);
      console.log('- Grupos seleccionados:', Array.from(gruposSeleccionados));
      console.log('- Grupos filtrados disponibles:', gruposFiltrados.map(g => ({ id: g.Id, name: g.Name })));

      // Verificar que todos los grupos seleccionados estén en gruposFiltrados (grupos permitidos)
      const gruposPermitidos = new Set(gruposFiltrados.map(g => g.Id));
      const gruposNoPermitidos = Array.from(gruposSeleccionados).filter(id => !gruposPermitidos.has(id));
      
      console.log('- Grupos permitidos:', Array.from(gruposPermitidos));
      console.log('- Grupos no permitidos:', gruposNoPermitidos);
      
      if (gruposNoPermitidos.length > 0) {
        error = `You don't have permission to access some selected groups: ${gruposNoPermitidos.join(', ')}`;
        console.error('Error de permisos:', error);
        return;
      }

      console.log('Validación de permisos exitosa - procediendo con la búsqueda');

      error = null;
      loading = true;
      noResults = false;

      const token = GetCachedToken();
      if (!token) {
        error = 'No token available';
        return;
      }

      if (!meStore?.Id) {
        error = 'User ID not available';
        return;
      }

      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      fin.setHours(23, 59, 59);

      // Limpiar resultados anteriores
      console.log('Limpiando datos anteriores...');
      resumenPorGrupo = {};
      console.log('Grupos seleccionados para búsqueda:', Array.from(gruposSeleccionados));
      let hasError = false;
      let errorMessage = '';

      // Hacer una llamada por cada grupo seleccionado
      for (const groupId of gruposSeleccionados) {
        try {
          const response = await service.walletapi.sumByDateDebit({
            Token: token,
            Data: {
              Datestart: inicio,
              Dateend: fin,
              GroupIds: [groupId],
              TransactionType: undefined,
              PlayerId: undefined,
              GameId: undefined,
              UserId: meStore.Id,
              ParentId: undefined,
              PlayerSessionId: undefined
            }
          });

          console.log(`Respuesta para grupo ${groupId}:`, response);

          if (response?.Success && response.Data) {
            console.log(`Datos recibidos para grupo ${groupId}:`, response.Data);
            resumenPorGrupo[groupId] = response.Data;
          } else {
            hasError = true;
            if (response?.ErrorMessage) {
              errorMessage = response.ErrorMessage;
            } else {
              errorMessage = 'No valid data received from API';
            }
            // Crear un objeto vacío para este grupo
            resumenPorGrupo[groupId] = {
              TotalDepositMoney: { InCents: 0 },
              TotalWithdrawMoney: { InCents: 0 },
              TotalDepositPromo: { InCents: 0 },
              TotalWithdrawPromo: { InCents: 0 },
              TotalDebitMoney: { InCents: 0 },
              TotalCreditMoney: { InCents: 0 },
              TotalEndRoundMoney: { InCents: 0 },
              TotalCancelBetMoney: { InCents: 0 },
              TotalDepositCancelMoney: { InCents: 0 },
              TotalWithdrawCancelMoney: { InCents: 0 },
              TotalWithdrawWaitingMoney: { InCents: 0 },
              TotalPromoCanceledMoney: { InCents: 0 },
              TotalPromoExpiredMoney: { InCents: 0 },
              GroupId: groupId,
              GroupIds: [groupId],
              ParentId: undefined,
              Games: []
            };
          }
        } catch (err) {
          console.error(`Error obteniendo datos para grupo ${groupId}:`, err);
          hasError = true;
          errorMessage = 'Error obtaining transaction summary';
          // Crear un objeto vacío para este grupo
          resumenPorGrupo[groupId] = {
            TotalDepositMoney: { InCents: 0 },
            TotalWithdrawMoney: { InCents: 0 },
            TotalDepositPromo: { InCents: 0 },
            TotalWithdrawPromo: { InCents: 0 },
            TotalDebitMoney: { InCents: 0 },
            TotalCreditMoney: { InCents: 0 },
            TotalEndRoundMoney: { InCents: 0 },
            TotalCancelBetMoney: { InCents: 0 },
            TotalDepositCancelMoney: { InCents: 0 },
            TotalWithdrawCancelMoney: { InCents: 0 },
            TotalWithdrawWaitingMoney: { InCents: 0 },
            TotalPromoCanceledMoney: { InCents: 0 },
            TotalPromoExpiredMoney: { InCents: 0 },
            GroupId: groupId,
            GroupIds: [groupId],
            ParentId: undefined,
            Games: []
          };
        }
      }

      if (hasError) {
        error = errorMessage;
      } else {
        error = null;
      }

      // Marcar que se han cargado datos iniciales
      hasInitialData = true;
      console.log('Datos cargados para grupos:', Object.keys(resumenPorGrupo));
      console.log('Resumen por grupo:', resumenPorGrupo);

    } catch (err) {
      console.error('Error obtaining summary:', err);
      error = 'Error obtaining transaction summary';
    } finally {
      loading = false;
    }
  }

  function toggleGrupoButton(id: number): void {
    console.log('Toggle grupo:', id);
    manualSelection = true; // Indicar que es una selección manual
    
    // Verificar que el usuario tenga permiso para acceder a este grupo
    const gruposPermitidos = new Set(gruposFiltrados.map(g => g.Id));
    if (!gruposPermitidos.has(id)) {
      console.log('Usuario no tiene permiso para acceder al grupo:', id);
      return;
    }
    
    const newGruposSeleccionados = new Set<number>(gruposSeleccionados);
    const grupoActual = grupos.find(g => g.Id === id);
    
    if (!grupoActual) return;

    // Selección simple: solo el grupo específico, sin hijos automáticos
    if (newGruposSeleccionados.has(id)) {
      // Si el grupo estaba seleccionado, deseleccionarlo
      newGruposSeleccionados.delete(id);
    } else {
      // Si el grupo no estaba seleccionado, seleccionarlo
      newGruposSeleccionados.add(id);
    }

    gruposSeleccionados = newGruposSeleccionados;
    console.log('Grupos seleccionados actualizados:', Array.from(gruposSeleccionados));
    
    // Resetear la bandera después de un breve delay
    // NO ejecutar búsqueda automática - el usuario debe hacer clic en "Search"
    setTimeout(() => {
      manualSelection = false;
    }, 100);
  }

  function toggleTodosButton(): void {
    console.log('Toggle todos');
    manualSelection = true; // Indicar que es una selección manual
    
    if (todosSeleccionados) {
      // Si todos están seleccionados, deseleccionar todos
      gruposSeleccionados = new Set<number>();
    } else {
      // Si no todos están seleccionados, seleccionar solo los grupos permitidos
      const newGruposSeleccionados = new Set<number>();
      gruposFiltrados.forEach(grupo => {
        newGruposSeleccionados.add(grupo.Id);
      });
      gruposSeleccionados = newGruposSeleccionados;
    }
    
    // Forzar actualización de la vista
    gruposSeleccionados = gruposSeleccionados; 
    console.log('Grupos seleccionados:', Array.from(gruposSeleccionados));
    
    // Resetear la bandera después de un breve delay
    // NO ejecutar búsqueda automática - el usuario debe hacer clic en "Search"
    setTimeout(() => {
      manualSelection = false;
    }, 100);
  }

  // Función para manejar el cambio de checkbox
  function handleCheckboxChange(event: Event, id: number): void {
    event.stopPropagation(); // Evitar que el click se propague
    toggleGrupoButton(id);
  }

  // Función para manejar la selección de todos los grupos
  function handleSelectAll(event: Event): void {
    event.stopPropagation(); // Evitar que el click se propague
    toggleTodosButton();
  }

  $: {
    if (gruposFiltrados.length > 0 && !manualSelection) {
      // Actualizar todosSeleccionados para que refleje el estado real
      todosSeleccionados = gruposFiltrados.every(grupo => gruposSeleccionados.has(grupo.Id));
    }
  }

  $: {
    if (currentGroupId) {
      filtrarGrupos();
    }
  }

  // Reactive statement para ejecutar búsqueda automática cuando cambien los grupos seleccionados
  // Comentado para evitar conflictos con las funciones de selección
  /*
  $: {
    if (hasInitialData && gruposSeleccionados.size > 0 && !manualSelection && fechaInicio && fechaFin) {
      console.log('Grupos seleccionados cambiaron, ejecutando búsqueda automática...');
      // Usar setTimeout para evitar múltiples ejecuciones
      setTimeout(() => {
        sumByDateDebit();
      }, 100);
    }
  }
  */

  // Función para obtener todos los grupos con su estructura jerárquica
  function getAllGroupsWithStructure(grupos: Grupo[]): Grupo[] {
    const result: Grupo[] = [];
    
    function addGroup(grupo: Grupo, level: number = 0) {
      // Crear una nueva copia del grupo con el nivel
      const grupoConNivel = { ...grupo, level };
      result.push(grupoConNivel);
      
      // Buscar hijos donde el ParentId coincida con el Id del grupo actual
      const children = grupos.filter(g => g.ParentId && g.ParentId === grupo.Id);
      // Ordenar los hijos por nombre si es necesario
      children.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
      children.forEach(child => addGroup(child, level + 1));
    }

    // Comenzar con los grupos padre (aquellos sin ParentId)
    const parentGroups = grupos.filter(g => !g.ParentId);
    // Ordenar los grupos padre por nombre si es necesario
    parentGroups.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
    parentGroups.forEach(grupo => addGroup(grupo));
    
    return result;
  }

  // Función para obtener el nombre del grupo con indentación
  function getGroupDisplayName(grupo: Grupo): string {
    const meStore = get(me);
    
    // Si es el grupo actual del usuario
    if (grupo.Id === currentGroupId) {
      return `📁 ${grupo.Name || ''}`;
    }
    
    // Si es Cashier y es el grupo padre
    if (meStore?.Role === "Cashier" && meStore?.GroupId && grupo.Id === grupos.find(g => g.Id === meStore.GroupId)?.ParentId) {
      return `⬆️ ${grupo.Name || ''}`;
    }
    
    // Si es Admin y es un subgrupo
    if (meStore?.Role === "Admin" && grupo.ParentId === currentGroupId) {
      return `└─ ${grupo.Name || ''}`;
    }
    
    // Para otros grupos con nivel
    if (grupo.level && grupo.level > 0) {
      // Para hijos directos (nivel 1), usar una línea
      if (grupo.level === 1) {
        return `└─ ${grupo.Name || ''}`;
      }
      
      // Para niveles más profundos, usar espacios para alinear y una línea
      const spaces = '   '.repeat(grupo.level - 1);
      return `${spaces}└─ ${grupo.Name || ''}`;
    }
    
    // Para grupos raíz sin nivel específico
    return grupo.Name || '';
  }

  // Función para obtener el nombre del rol del usuario
  function getUserRoleDisplayName(role: string): string {
    switch (role) {
      case 'Owner':
        return 'Owner (All Groups)';
      case 'Admin':
        return 'Admin (Assigned Group + Subgroups)';
      case 'Cashier':
        return 'Cashier (Assigned Group + Parent Group)';
      default:
        return role;
    }
  }

  // Actualizar gruposFiltrados cuando cambien los grupos (solo para Owner)
  $: {
    if (grupos.length > 0) {
      const meStore = get(me);
      if (meStore?.Role === "Owner") {
        gruposFiltrados = getAllGroupsWithStructure(grupos);
        console.log('Owner - Grupos estructurados actualizados:', gruposFiltrados);
      } else if (meStore?.Role === "Admin" || meStore?.Role === "Cashier") {
        // Para Admin y Cashier, mantener el filtrado basado en su grupo asignado
        if (currentGroupId) {
          filtrarGrupos();
        }
      }
    }
  }
</script>

<div class="ui container">

  <!-- Información de permisos del usuario -->
  <div class="ui segment">
    <h3 class="ui header">{$translate('Sums')}</h3>
    {#if get(me)}
      <!-- {@const meStore = get(me)}
      <div class="ui info message">
        <div class="header">Current User: {meStore?.FirstName} {meStore?.LastName}</div>
        <p><strong>Role:</strong> {getUserRoleDisplayName(meStore?.Role || '')}</p>
        {#if meStore?.GroupId}
          <p><strong>Assigned Group ID:</strong> {meStore.GroupId}</p>
        {/if}
        <p><strong>Available Groups:</strong> {gruposFiltrados.length}</p>
        <p><strong>Selected Groups:</strong> {gruposSeleccionados.size}</p> -->
        <!-- {#if meStore?.Role === 'Cashier'}
          <div class="ui warning message" style="margin-top: 1em;">
            <div class="header">{$translate('Available Groups')}</div>
            <p>{$translate('You can see players from your group, parent group, and subgroups.')}</p>
          </div>
        {/if} -->
      <!-- </div> -->
    {/if}
  </div>

  <div class="ui segment">
    <div class="ui form">
      <div class="fields">
        <div class="field">
          <label>{$translate('Start Date')}</label>
          <div class="ui input">
            <input 
              type="date" 
              bind:value={fechaInicio}
              max={fechaFin}
            >
          </div>
        </div>
        <div class="field">
          <label>{$translate('End Date')}</label>
          <div class="ui input">
            <input 
              type="date" 
              bind:value={fechaFin}
              min={fechaInicio}
            >
          </div>
        </div>
        <div class="field">
          <label>{$translate('Groups')}</label>
          <div 
            class="ui fluid multiple search selection dropdown" 
            style="width: 100%; margin-bottom: 1em; position: relative;"
            bind:this={dropdownRef}
            on:click={toggleMenu}
          >
            <input type="hidden" name="groups">
            <i class="dropdown icon"></i>
            <div class="default text" style="font-size: 0.9em;">Multiple groups ({gruposSeleccionados.size})</div>
            <div class="menu" class:visible={menuVisible}>
              <div class="item" on:click={handleSelectAll}>
                <div class="ui checkbox">
                  <input 
                    type="checkbox" 
                    checked={todosSeleccionados}
                  >
                </div>
                <span style="font-size: 0.9em;">{$translate('Select All')}</span>
              </div>
              {#each gruposFiltrados as grupo}
                <div 
                  class="item"
                  class:selected={gruposSeleccionados.has(grupo.Id)}
                  style="display: flex; align-items: center; padding: 0.4em 0.8em;"
                  on:click={handleItemClick}
                >
                  <div class="ui checkbox">
                    <input 
                      type="checkbox" 
                      checked={gruposSeleccionados.has(grupo.Id)}
                      on:change={(e) => handleCheckboxChange(e, grupo.Id)}
                    >
                  </div>
                  <span style="flex-grow: 1; white-space: pre; font-size: 0.9em;">
                    {getGroupDisplayName(grupo)}
                    {#if grupo.Id === currentGroupId}
                      <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                        {$translate('Current')}
                      </div>
                    {/if}
                  </span>
                </div>
              {/each}
            </div>
          </div>
        </div>
        <div class="field search-button-field">
          <label>&nbsp;</label>
          <button 
            class="ui primary button large fluid"
            on:click={sumByDateDebit}
            disabled={loading || !fechaInicio || !fechaFin || gruposSeleccionados.size === 0}
          >
            {#if loading}
              <i class="spinner loading icon"></i> {$translate('Loading')}...
            {:else}
              <i class="search icon"></i> {$translate('Search')}
            {/if}
          </button>
        </div>
      </div>
    </div>
  </div>

  {#if Object.keys(resumenPorGrupo).length > 0}
    <div class="ui segment">
  {#if error}
    <div class="ui negative message">
      <p>{error}</p>
    </div>
  {/if}
      {#if noResults}
        <div class="ui warning message">
          <div class="header"> {$translate('No results found')}</div>
          <p> {$translate('No data available for the selected period')}.</p>
        </div>
      {/if}
      
      <!-- Tabla principal de resumen -->
      <div class="ui header" style="display: flex; justify-content: space-between; align-items: center;">
        <h3 class="content">{$translate('Transaction Summary')}</h3>
        <button 
          class="ui small button"
          on:click={() => showMoreColumns = !showMoreColumns}
        >
          {#if showMoreColumns}
            <i class="chevron up icon"></i> {$translate('Less')}
          {:else}
            <i class="chevron down icon"></i> {$translate('More')}
          {/if}
        </button>
      </div>
      <table class="ui celled table">
        <thead>
          <tr>
            <th>{$translate('Groups')}</th>
            <th>{$translate('Deposit')}</th>
            <th>{$translate('Withdraw')}</th>
            <th>{$translate('Deposit Profit')}</th>
            <th>{$translate('Bet')}</th>
            <th>{$translate('Win')}</th>
            <th>{$translate('Profit')}</th>
            {#if showMoreColumns}
              <th>{$translate('End Round')}</th>
              <th>{$translate('Cancel Bet')}</th>
              <th>{$translate('Deposit Cancel')}</th>
              <th>{$translate('Withdraw Cancel')}</th>
            {/if}
          </tr>
        </thead>
        <tbody>
          {#each grupos as grupo}
            {#if gruposSeleccionados.has(grupo.Id)}
              {@const resumen = resumenPorGrupo[grupo.Id]}
              <tr>
                <td>
                  <span class="group-name">
                    {grupo.Name}
                    {#if grupo.Id === currentGroupId}
                      <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                        {$translate('Current')}
                      </div>
                    {/if}
                  </span>
                </td>
                <td class="positive">{formatMoney(resumen?.TotalDepositMoney)}</td>
                <td class="negative">{formatMoney(resumen?.TotalWithdrawMoney)}</td>
                <td class:negative={(resumen?.TotalDepositMoney?.InCents || 0) - (resumen?.TotalWithdrawMoney?.InCents || 0) < 0}
                    class:positive={(resumen?.TotalDepositMoney?.InCents || 0) - (resumen?.TotalWithdrawMoney?.InCents || 0) > 0}>
                  {formatMoneyWithSign({InCents: (resumen?.TotalDepositMoney?.InCents || 0) - (resumen?.TotalWithdrawMoney?.InCents || 0)})}
                </td>
                <td>{formatMoney(resumen?.TotalDebitMoney)}</td>
                <td>{formatMoney(resumen?.TotalCreditMoney)}</td>
                <td class:negative={(resumen?.TotalDebitMoney?.InCents || 0) - (resumen?.TotalCreditMoney?.InCents || 0) < 0}
                    class:positive={(resumen?.TotalDebitMoney?.InCents || 0) - (resumen?.TotalCreditMoney?.InCents || 0) >= 0}>
                  {formatMoneyWithSign({InCents: (resumen?.TotalDebitMoney?.InCents || 0) - (resumen?.TotalCreditMoney?.InCents || 0)})}
                </td>
                {#if showMoreColumns}
                  <td>{formatMoney(resumen?.TotalEndRoundMoney)}</td>
                  <td>{formatMoney(resumen?.TotalCancelBetMoney)}</td>
                  <td class="warning">{formatMoney(resumen?.TotalDepositCancelMoney)}</td>
                  <td class="warning">{formatMoney(resumen?.TotalWithdrawCancelMoney)}</td>
                {/if}
              </tr>
            {/if}
          {/each}
          {#if gruposSeleccionados.size > 0}
            {@const totals = Array.from(gruposSeleccionados).reduce((acc, groupId) => {
              const resumen = resumenPorGrupo[groupId];
              return {
                deposit: acc.deposit + (resumen?.TotalDepositMoney?.InCents || 0),
                withdraw: acc.withdraw + (resumen?.TotalWithdrawMoney?.InCents || 0),
                bet: acc.bet + (resumen?.TotalDebitMoney?.InCents || 0),
                win: acc.win + (resumen?.TotalCreditMoney?.InCents || 0),
                endRound: acc.endRound + (resumen?.TotalEndRoundMoney?.InCents || 0),
                cancelBet: acc.cancelBet + (resumen?.TotalCancelBetMoney?.InCents || 0),
                depositCancel: acc.depositCancel + (resumen?.TotalDepositCancelMoney?.InCents || 0),
                withdrawCancel: acc.withdrawCancel + (resumen?.TotalWithdrawCancelMoney?.InCents || 0)
              };
            }, {
              deposit: 0,
              withdraw: 0,
              bet: 0,
              win: 0,
              endRound: 0,
              cancelBet: 0,
              depositCancel: 0,
              withdrawCancel: 0
            })}
            <tr class="total-row">
              <td><strong>Totals</strong></td>
              <td class="positive"><strong>{formatMoney({InCents: totals.deposit})}</strong></td>
              <td class="negative"><strong>{formatMoney({InCents: totals.withdraw})}</strong></td>
              <td class:negative={totals.deposit - totals.withdraw < 0}
                  class:positive={totals.deposit - totals.withdraw > 0}>
                <strong>{formatMoneyWithSign({InCents: totals.deposit - totals.withdraw})}</strong>
              </td>
              <td><strong>{formatMoney({InCents: totals.bet})}</strong></td>
              <td><strong>{formatMoney({InCents: totals.win})}</strong></td>
              <td class:negative={totals.bet - totals.win < 0}
                  class:positive={totals.bet - totals.win >= 0}>
                <strong>{formatMoneyWithSign({InCents: totals.bet - totals.win})}</strong>
              </td>
              {#if showMoreColumns}
                <td><strong>{formatMoney({InCents: totals.endRound})}</strong></td>
                <td><strong>{formatMoney({InCents: totals.cancelBet})}</strong></td>
                <td class="warning"><strong>{formatMoney({InCents: totals.depositCancel})}</strong></td>
                <td class="warning"><strong>{formatMoney({InCents: totals.withdrawCancel})}</strong></td>
              {/if}
            </tr>
          {/if}
        </tbody>
      </table>

      <!-- Nueva tabla para datos del juego -->
      <h3 class="ui header">
        <div class="content">
          {$translate('Game Counters')}
        </div>
      </h3>
      <div class="ui segment">
        <table class="ui celled table game-counters">
        <thead>
          <tr>
              <th class="group-column">{$translate('Group Name')}</th>
              <th class="game-column">  {$translate('Game Name')}</th>
              <th class="number-column">{$translate('Total Rounds')}</th>
              <th class="number-column">{ $translate('Total Players')}</th>
          </tr>
        </thead>
        <tbody>
            {#each grupos as grupo}
              {#if gruposSeleccionados.has(grupo.Id) && resumenPorGrupo[grupo.Id]}
                {@const resumen = resumenPorGrupo[grupo.Id]}
                {#if resumen.Games && resumen.Games.length > 0}
                  {#each resumen.Games as game, index}
                    <tr class:highlight={grupo.Id === currentGroupId}>
                      <td class="group-column">
                        {#if index === 0}
                          <div class="group-header">
                            {grupo.Name}
                            {#if grupo.Id === currentGroupId}
                              <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                                {$translate('Current')}
                              </div>
                            {/if}
                          </div>
                        {/if}
                      </td>
                      <td class="game-column">{game.GameName || 'N/A'}</td>
                      <td class="number-column">{game.TotalJugadas || 0}</td>
                      <td class="number-column">{game.TotalJugadores || 0}</td>
              </tr>
            {/each}
          {:else}
                  <tr class:highlight={grupo.Id === currentGroupId}>
                    <td class="group-column">
                      <div class="group-header">
                        {grupo.Name}
                        {#if grupo.Id === currentGroupId}
                          <div class="ui mini label">{$translate('Current')}</div>
                        {/if}
                      </div>
                    </td>
                    <td colspan="3" class="no-data">{$translate('No games data available')}</td>
            </tr>
          {/if}
              {/if}
            {/each}
        </tbody>
      </table>
      </div>

      <!-- Tabla de resumen de PromotionTransaction -->
      <h3 class="ui header">
        <div class="content">{$translate('Promotion Transactions Summary')}</div>
      </h3>
      <table class="ui celled table">
        <thead>
          <tr>
            <th>{$translate('Groups')}</th>
            <th>{$translate('Deposit')}</th>
            <th>{$translate('Withdraw')}</th>
          </tr>
        </thead>
        <tbody>
          {#each grupos as grupo}
            {#if gruposSeleccionados.has(grupo.Id)}
              {@const resumen = resumenPorGrupo[grupo.Id]}
              <tr>
                <td>
                  <span class="group-name">
                    {grupo.Name}
                    {#if grupo.Id === currentGroupId}
                      <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                        {$translate('Current')}
                      </div>
                    {/if}
                  </span>
                </td>
                <td>{formatMoney(resumen?.TotalDepositPromo)}</td>
                <td>{formatMoney(resumen?.TotalWithdrawPromo)}</td>
              </tr>
            {/if}
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<style>
   .ui.dropdown {
    min-height: 2.2em !important;
    position: relative;
    padding: 0.4em 0.8em;
    cursor: pointer;
    z-index: 1000;
  }

  .ui.dropdown .menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: 0.28571429rem;
    box-shadow: 0 2px 4px 0 rgba(34,36,38,.15);
    z-index: 1001;
    max-height: 250px;
    overflow-y: auto;
    margin-top: 0.3em !important;
    width: 100%;
  }

  .ui.dropdown .menu > .item {
    position: relative;
    z-index: 1002;
    background: white;
    padding: 0.4em 0.8em !important;
    line-height: 1.2;
  }

  .ui.dropdown .menu > .item:hover {
    background: rgba(0,0,0,.05);
  }

  .ui.dropdown .menu > .item.selected {
    background: rgba(0,0,0,.03) !important;
  }

  .ui.dropdown .text {
    margin-right: 2em;
    line-height: 1.2;
    display: flex !important;
    align-items: center !important;
  }

  .ui.checkbox {
    margin-right: 8px !important;
    min-width: 14px !important;
  }

  .ui.checkbox input[type="checkbox"] {
    width: 14px !important;
    height: 14px !important;
  }

  .ui.dropdown .menu > .item span {
    font-family: monospace;
    padding: 0.2em 0;
  }

  .ui.dropdown .menu.visible {
    display: block;
  }

  /* Ajustar el tamaño del contenedor del dropdown */
  .field .ui.dropdown {
    min-width: 200px;
    max-width: 250px;
  }

  .ui.table {
    position: relative;
    z-index: 1;
  }

  .ui.checkbox {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    margin: 0 !important;
    margin-right: 10px !important;
    min-width: 16px !important;
  }

  .ui.checkbox input[type="checkbox"] {
    position: relative !important;
    margin: 0 !important;
    width: 16px !important;
    height: 16px !important;
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
    z-index: 1 !important;
    -webkit-appearance: checkbox !important;
    appearance: checkbox !important;
    cursor: pointer !important;
  }

  /* Estilos de los items del menú */
  .ui.dropdown .menu > .item {
    padding: 0.5em 1em !important;
    display: flex !important;
    align-items: center !important;
    cursor: pointer;
  }

  .ui.dropdown .menu > .item:hover {
    background: rgba(0,0,0,.05);
  }

  .ui.dropdown .menu > .item.selected {
    background: rgba(0,0,0,.03) !important;
  }

  /* Etiquetas */
  .ui.mini.label {
    margin-left: 0.5em;
    padding: 0.2em 0.5em;
    font-size: 0.7em;
    vertical-align: middle;
    display: inline-block;
    background: #e8e8e8;
    border-radius: 0.28571429rem;
  }

  /* Estilos de los inputs de fecha */
  .ui.input input[type="date"] {
    padding: 0.5em;
    font-size: 1em;
    width: 100%;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: 0.28571429rem;
  }

  /* Centrar textos de la tabla */
  .ui.table th,
  .ui.table td {
    text-align: center !important;
  }

  /* Mantener alineación izquierda para la columna de grupo */
  .ui.table td:first-child {
    text-align: left !important;
  }

  /* Estilos para la tabla de Game Counters */
  .game-counters {
    margin-top: 1em;
  }

  .game-counters th {
    background-color: #f8f9fa !important;
    font-weight: 600 !important;
  }

  .game-counters .group-column {
    width: 25% !important;
    text-align: left !important;
  }

  .game-counters .game-column {
    width: 35% !important;
    text-align: left !important;
  }

  .game-counters .number-column {
    width: 20% !important;
    text-align: right !important;
  }

  .game-counters .group-header {
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-weight: 500;
  }

  .game-counters .no-data {
    text-align: center !important;
    color: #999;
    font-style: italic;
  }

  .game-counters tr.highlight {
    background-color: #f8f9fa;
  }

  .game-counters tr.highlight td {
    background-color: #f8f9fa !important;
  }

  .game-counters .ui.mini.label {
    background-color: #e8e8e8;
    color: #666;
    font-weight: 500;
  }

  /* Estilos para el indicador de grupo actual */
  .ui.mini.label {
    margin-left: 0.5em;
    padding: 0.2em 0.5em;
    font-size: 0.7em;
    vertical-align: middle;
    display: inline-block;
    border-radius: 0.28571429rem;
  }

  /* Estilos para el nombre del grupo */
  .group-name {
    display: flex;
    align-items: center;
    font-weight: 500;
  }

  /* Estilos para el header del grupo en la tabla de juegos */
  .group-header {
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-weight: 500;
  }

  /* Estilos para el mensaje de información del usuario */


</style>